import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import { siteInfo } from '@/mocks/tvSeries';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://caomin.tv',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
            item: 'https://caomin.tv/about',
          },
        ],
      },
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: 'https://caomin.tv',
        description: siteInfo.description,
        email: siteInfo.email,
        foundingDate: siteInfo.founded,
        slogan: siteInfo.slogan,
        sameAs: ['https://caomin.tv'],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '草民影院支持哪些类型的电视剧？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '草民影院涵盖国产剧、韩剧、美剧、日剧、泰剧、动漫等多种类型电视剧，满足不同观众的观剧需求。',
            },
          },
          {
            '@type': 'Question',
            name: '草民影院的画质如何？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '草民影院支持高清1080P画质播放，即点即看，无需等待缓冲，为您带来极致的观影体验。',
            },
          },
          {
            '@type': 'Question',
            name: '剧集更新速度快吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '我们拥有专业的内容运营团队，热门剧集每日同步更新，确保用户不错过任何精彩内容。',
            },
          },
          {
            '@type': 'Question',
            name: '如何联系草民影院？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过发送邮件至contact@caomin.tv与我们取得联系，我们会在24小时内回复。',
            },
          },
        ],
      },
      {
        '@type': 'WebPage',
        name: `关于${siteInfo.name}`,
        description: `${siteInfo.name}品牌介绍、用户口碑与联系方式。${siteInfo.slogan}，自${siteInfo.founded}年起为观众提供优质在线观剧体验。`,
        url: 'https://caomin.tv/about',
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={`关于${siteInfo.name} - 品牌介绍与联系方式`}
        description={`${siteInfo.name}品牌介绍、用户口碑与联系方式。${siteInfo.slogan}，自${siteInfo.founded}年起为观众提供优质在线观剧体验。致力于成为最受欢迎的电视剧在线播放平台。`}
        keywords="草民影院,关于我们,品牌介绍,联系方式,电视剧平台,在线观剧"
        canonicalUrl="https://caomin.tv/about"
        schema={aboutSchema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-14">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
              关于{siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.slogan} — 自{siteInfo.founded}年起，致力于为观众提供优质的在线观剧体验
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  草民影院成立于{siteInfo.founded}年，是一家专注于电视剧在线播放的综合平台。我们致力于为全国乃至全球华语观众提供最全面、最及时、最优质的电视剧内容。
                </p>
                <p>
                  平台涵盖国产剧、韩剧、美剧、日剧、泰剧、动漫等多元类型，满足不同观众的观剧需求。从经典老剧到最新热播，从古装仙侠到现代都市，草民影院始终秉持"高清不卡，即点即看"的理念，为用户打造流畅舒适的观剧体验。
                </p>
                <p>
                  我们拥有一支专业的内容运营团队，每日更新热门剧集，确保用户不错过任何精彩内容。同时，我们注重用户体验，持续优化播放技术和界面设计，让每一位用户都能轻松找到心仪的剧集。
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl h-[300px] md:h-[400px] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=A%20modern%20media%20streaming%20control%20room%20with%20multiple%20screens%20displaying%20various%20drama%20posters%2C%20warm%20ambient%20lighting%2C%20professional%20workspace%20atmosphere%2C%20clean%20minimal%20design%2C%20soft%20golden%20and%20warm%20gray%20tones%2C%20cinematic%20workplace%20photography&width=1200&height=800&seq=about-cinema&orientation=landscape"
                alt="草民影院工作环境"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              { number: '10000+', label: '剧集资源' },
              { number: '500万+', label: '活跃用户' },
              { number: '99.9%', label: '播放成功率' },
              { number: '24h', label: '更新保障' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">{stat.number}</div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="mb-14">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '追剧达人小王',
                  text: '草民影院真的是我每天必刷的网站，更新速度超快，画质也很清晰，追剧体验一级棒！强烈推荐给所有喜欢看剧的朋友。',
                  rating: 5,
                },
                {
                  name: '影视爱好者小李',
                  text: '剧集资源很全，分类也很清晰，找剧非常方便。而且没有烦人的广告弹窗，观影体验比很多大平台都好。',
                  rating: 5,
                },
                {
                  name: '韩剧迷妹小张',
                  text: '韩剧更新太及时了！熬夜追剧的快乐就是草民影院给的。画质清晰流畅，即点即看，真的很赞。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="p-6 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 text-xs font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center py-10 bg-background-100 rounded-xl border border-background-200/70 mb-14">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
              如果您有任何问题或建议，欢迎通过以下方式联系我们
            </p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <a
                href="mailto:contact@caomin.tv"
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-base"></i>
                </span>
                发送邮件
              </a>
              <Link
                to="/"
                className="flex items-center gap-2 px-5 py-2.5 bg-background-50 text-foreground-700 rounded-lg text-sm font-semibold border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line text-base"></i>
                </span>
                返回首页
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}